import React, { useState, useEffect, useMemo } from 'react';
import ModelsAPI from '../../api/models';
import {
  ChatProvider,
  CHAT_PROVIDERS,
  ProviderDefinition,
  providerRequiresModel,
} from '../../api/structs/providers';
import { AIModel, formatCreditPrice } from '../../api/structs/model';
import { Select, ModelPickerSheet } from '../ui';

interface ChatProviderSelectorProps {
  provider: ChatProvider;
  model: string;
  onProviderChange: (provider: ChatProvider) => void;
  onModelChange: (model: string) => void;
  className?: string;
  /**
   * Providers this call site may offer. Defaults to every chat provider, but
   * panes whose backend only speaks to one of them (the AI Editor) must pass a
   * narrower list so an unsupported option is never selectable in the first
   * place.
   */
  providers?: ProviderDefinition[];
}

const ChatProviderSelector: React.FC<ChatProviderSelectorProps> = ({
  provider,
  model,
  onProviderChange,
  onModelChange,
  className = '',
  providers = CHAT_PROVIDERS,
}) => {
  const [models, setModels] = useState<AIModel[]>([]);
  const [loading, setLoading] = useState(false);
  // "The catalog answered", as distinct from "we have no models". Only a
  // successful fetch may drive the selection, so a network failure or a
  // still-in-flight request can never clobber the persisted choice.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!providerRequiresModel(provider)) {
      setModels([]);
      return;
    }

    let cancelled = false;
    const fetchModels = async () => {
      setLoading(true);
      try {
        const chatModels = await ModelsAPI.getChatModels();
        if (cancelled) return;
        setModels(chatModels);
        setLoaded(true);
      } catch (error) {
        console.error('Failed to fetch models:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchModels();
    return () => {
      cancelled = true;
    };
  }, [provider]);

  // The chat catalog is one flat list across providers, so narrow it to the
  // provider that is actually selected. A model with no `provider` field is
  // kept either way (registry-lean/older entries shouldn't vanish). There is
  // deliberately NO "show everything instead" fallback: an empty picker is the
  // honest signal that this provider has nothing to offer, and showing another
  // provider's models here would let the user send a mismatched pair.
  const visibleModels = useMemo(
    () => models.filter((m) => !m.provider || m.provider === provider),
    [models, provider]
  );

  useEffect(() => {
    if (!providerRequiresModel(provider)) {
      return;
    }
    // Nothing loaded yet, or a catalog that came back completely empty
    // (registry down / not deployed): leave the persisted selection alone.
    if (!loaded || models.length === 0) {
      return;
    }
    if (model && visibleModels.some((m) => m.id === model)) {
      return;
    }

    // Empty selection, or one belonging to the provider we just switched away
    // from. Land on something this provider can actually serve — and clear it
    // outright when it can serve nothing, so no mismatched pair is sendable.
    const next = visibleModels[0]?.id ?? '';
    if (next !== model) {
      onModelChange(next);
    }
  }, [provider, model, models, visibleModels, loaded, onModelChange]);

  const dropdownOptions = visibleModels.map((m) => ({
    value: m.id,
    label: m.name,
    sublabel: m.id,
    rightLabel: formatCreditPrice(m),
  }));

  return (
    <div className={`flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 min-w-0 ${className}`}>
      <div className="w-full sm:w-[9rem] sm:flex-shrink-0">
        <Select
          options={providers}
          value={provider}
          onChange={(e) => onProviderChange(e.target.value as ChatProvider)}
          selectSize="sm"
          className="w-full min-w-0"
        />
      </div>

      {providerRequiresModel(provider) && (
        <div className="w-full sm:flex-1">
          <ModelPickerSheet
            options={dropdownOptions}
            value={model}
            onChange={onModelChange}
            placeholder="Select model"
            searchable
            loading={loading}
            chipClassName="text-xs"
            ariaLabel="Chat model"
          />
        </div>
      )}
    </div>
  );
};

export default ChatProviderSelector;
